import { FC, useState } from "react";
import { Navigate } from "react-router";

import { ThreediModel } from "@nens/threedi/openapi-client";
import * as api from "@nens/threedi/openapi-client";

import { APP_BASE_HREF } from "../App";
import { useThreediModel } from "../queries/threedimodels";

import InternalLink from "./InternalLink";
// We have a separate component for it so that the name of the model can be fetched and shown

interface ThreediModelLinkProps {
  threediModelId?: number;
  threediModel?: ThreediModel;
  onClick?: () => void;
}

const ThreediModelLink: FC<ThreediModelLinkProps> = ({ threediModelId, threediModel, onClick }) => {
  const threediModelResponse = useThreediModel(threediModelId);

  if (!threediModel && threediModelResponse.isFetching) return null;

  if (threediModel || threediModelResponse.isSuccess) {
    const model = threediModel || threediModelResponse.data;
    if (!model) {
      return <span title="Model not found">Not found</span>;
    }
    if (model.schematisationId) {
      const url = model.revisionId
        ? `${APP_BASE_HREF}/schematisations/${model.schematisationId}/${model.revisionId}`
        : `${APP_BASE_HREF}/schematisations/${model.schematisationId}`;
      return (
        <InternalLink to={url} onClick={onClick}>
          <span title={model.name}>{model.name}</span>
        </InternalLink>
      );
    } else {
      return <span title={model.name}>{model.name}</span>;
    }
  } else {
    return <span title="Model not found, probably permission denied">Not found</span>;
  }
};

export default ThreediModelLink;

export const ThreediModelQueryOnClickLink: FC<{ simulation: api.Simulation | undefined }> = ({
  simulation,
}) => {
  // Use start redirect to render the RedirectToThreediModelLink component otherwise it will fetch N+1
  const [startRedirect, setStartRedirect] = useState(false);
  if (!simulation) return null;

  if (simulation.threedimodelId === undefined) {
    return <span title="No model linked">No model</span>;
  }

  const RedirectToThreediModelLink: FC<{ threediModelId: number }> = ({ threediModelId }) => {
    const { data, isLoading, isSuccess } = useThreediModel(threediModelId);

    if (isLoading) {
      return "Loading...";
    }

    if (!isSuccess || !data.schematisationId) {
      return <span title="Model not found, probably permission denied">Not found</span>;
    }

    const url = data.revisionId
      ? `${APP_BASE_HREF}/schematisations/${data.schematisationId}/${data.revisionId}`
      : `${APP_BASE_HREF}/schematisations/${data.schematisationId}`;

    return <Navigate to={url} />;
  };

  return (
    <InternalLink
      to=""
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setStartRedirect(true);
      }}
    >
      {startRedirect ? (
        <RedirectToThreediModelLink threediModelId={simulation.threedimodelId} />
      ) : (
        <span>Go to model</span>
      )}
    </InternalLink>
  );
};
